import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, CheckCircle2, Lock, Gamepad2, BookOpen, Users, Sparkles } from 'lucide-react';
import useDailyQuestStore from '../../stores/useDailyQuestStore';
import { useUserStore } from '../../stores/useUserStore';
import useArcadeStore from '../../stores/useArcadeStore';
import ArcadeModal from '../games/ArcadeModal';
import ClassLeaderboardModal from '../common/ClassLeaderboardModal';
import { QUEST_SCHEDULE, DAILY_BONUS_XP, TOTAL_QUEST_DAYS } from '../../config/questSchedule';
import { fireCelebrationConfetti } from '../../utils/confettiHelper';
import LexioMascot from '../mascot/LexioMascot';
import ParentPINGate from '../common/ParentPINGate';
import { STATION_NAMES } from '../../config/stationLabels';
import ChroniclesFAB from './ChroniclesFAB';
import DoorUnlockCeremony from './DoorUnlockCeremony';
import questMapBg from '../../assets/quest-map-forest.jpg';
import './QuestMap3D.css';

// Preload task zone chunks in background while student is exploring the Map
const preloadTaskZones = () => {
  try {
    import('../../modules/zones/StoryWorldZone');
    import('../../modules/zones/BattleArenaZone');
    import('../../modules/zones/CreatorStudioZone');
    import('../../modules/zones/BossBattleZone');
    import('../../modules/zones/InfoExchangeZone');
  } catch (_) {}
};
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(preloadTaskZones);
  } else {
    setTimeout(preloadTaskZones, 100);
  }
}

const PATH_MIDPOINTS = [
  { x: 28, y: 60, angle: -30 },
  { x: 47, y: 52, angle: -15 },
  { x: 62, y: 46, angle: -30 },
  { x: 75, y: 30, angle: -40 },
];

const PATH_MIDPOINTS_MOBILE = [
  { x: 25, y: 73, angle: -90 },
  { x: 25, y: 56, angle: -90 },
  { x: 25, y: 39, angle: -90 },
  { x: 25, y: 22, angle: -90 },
];

const STATIONS = [
  {
    id: 'explorer',
    index: 1,
    emoji: '📖',
    name: STATION_NAMES[1],
    color: '#3b82f6',
    glowColor: 'rgba(59, 130, 246, 0.65)',
    bgGradient: 'radial-gradient(circle at 35% 35%, #dbeafe 0%, #3b82f6 70%, #1d4ed8 100%)',
    position:       { x: 18, y: 74 },
    positionMobile: { x: 25, y: 82 },
    taskPositions:       [{ x: 8, y: 62 }, { x: 20, y: 56 }, { x: 30, y: 64 }],
    taskPositionsMobile: [{ x: 55, y: 79 }, { x: 70, y: 82 }, { x: 85, y: 85 }],
  },
  {
    id: 'science_lab',
    index: 2,
    emoji: '🔬',
    name: STATION_NAMES[2],
    color: '#10b981',
    glowColor: 'rgba(16, 185, 129, 0.65)',
    bgGradient: 'radial-gradient(circle at 35% 35%, #d1fae5 0%, #10b981 70%, #047857 100%)',
    position:       { x: 38, y: 52 },
    positionMobile: { x: 25, y: 65 },
    taskPositions:       [{ x: 28, y: 40 }, { x: 38, y: 34 }, { x: 48, y: 42 }],
    taskPositionsMobile: [{ x: 55, y: 62 }, { x: 70, y: 65 }, { x: 85, y: 68 }],
  },
  {
    id: 'wordhero',
    index: 3,
    emoji: '⚔️',
    name: STATION_NAMES[3],
    color: '#f59e0b',
    glowColor: 'rgba(245, 158, 11, 0.65)',
    bgGradient: 'radial-gradient(circle at 35% 35%, #fef3c7 0%, #f59e0b 70%, #b45309 100%)',
    position:       { x: 55, y: 58 },
    positionMobile: { x: 25, y: 48 },
    taskPositions:       [{ x: 45, y: 48 }, { x: 55, y: 42 }, { x: 65, y: 48 }],
    taskPositionsMobile: [{ x: 55, y: 45 }, { x: 70, y: 48 }, { x: 85, y: 51 }],
  },
  {
    id: 'creator',
    index: 4,
    emoji: '✍️',
    name: STATION_NAMES[4],
    color: '#8b5cf6',
    glowColor: 'rgba(139, 92, 246, 0.65)',
    bgGradient: 'radial-gradient(circle at 35% 35%, #ede9fe 0%, #8b5cf6 70%, #6d28d9 100%)',
    position:       { x: 68, y: 42 },
    positionMobile: { x: 25, y: 31 },
    taskPositions:       [{ x: 60, y: 30 }, { x: 70, y: 24 }, { x: 80, y: 30 }],
    taskPositionsMobile: [{ x: 55, y: 28 }, { x: 70, y: 31 }, { x: 85, y: 34 }],
  },
  {
    id: 'boss',
    index: 5,
    emoji: '🏰',
    name: STATION_NAMES[5],
    color: '#ef4444',
    glowColor: 'rgba(239, 68, 68, 0.65)',
    bgGradient: 'radial-gradient(circle at 35% 35%, #fee2e2 0%, #ef4444 70%, #b91c1c 100%)',
    position:       { x: 82, y: 34 },
    positionMobile: { x: 25, y: 15 },
    taskPositions:       [{ x: 70, y: 44 }, { x: 82, y: 48 }, { x: 92, y: 44 }],
    taskPositionsMobile: [{ x: 55, y: 12 }, { x: 70, y: 15 }, { x: 85, y: 18 }],
  },
];


function useIsPortrait() {
  const [isPortrait, setIsPortrait] = useState(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth
  );
  useEffect(() => {
    const handler = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);
  return isPortrait;
}

export default function QuestMap3D({ weekId, onToggleSidebar }) {
  const navigate = useNavigate();
  const isPortrait = useIsPortrait();
  const [expandedStation, setExpandedStation] = useState(null);
  const [showPIN, setShowPIN] = useState(false);
  const [showCoopModal, setShowCoopModal] = useState(false);
  const [showMobileQuickHub, setShowMobileQuickHub] = useState(false);
  const { isArcadeOpen, setArcadeOpen } = useArcadeStore();
  const [teacherOverride, setTeacherOverride] = useState(false);
  const [showNarrative, setShowNarrative] = useState(false);
  const [mascotMood, setMascotMood] = useState('waving');
  const [mascotQuoteIndex, setMascotQuoteIndex] = useState(0);
  const prevSuggestedIdxRef = useRef(-1);

  const { isQuestCompleted, getWeekQuestCount, isDayUnlocked, isDayComplete } = useDailyQuestStore();
  const weekQuestCount = getWeekQuestCount(weekId);
  const totalQuests = TOTAL_QUEST_DAYS * 3;
  const progressPercent = Math.round((weekQuestCount / totalQuests) * 100);

  // Moment 4: Door Unlock Ceremony state & session-change detection
  const [unlockCeremonyDay, setUnlockCeremonyDay] = useState(null);
  const prevDayCompleteRef = useRef({});
  const isFirstMountRef = useRef(true);

  useEffect(() => {
    if (isFirstMountRef.current) {
      // Seed existing completion on initial mount without triggering ceremony
      for (let d = 0; d < 5; d++) {
        const key = `${weekId}_d${d}`;
        prevDayCompleteRef.current[key] = Boolean(isDayComplete(weekId, d + 1));
      }
      isFirstMountRef.current = false;
      return;
    }

    // Detect newly completed day during this active session
    for (let d = 0; d < 5; d++) {
      const key = `${weekId}_d${d}`;
      const isNowComplete = Boolean(isDayComplete(weekId, d + 1));
      const wasComplete = Boolean(prevDayCompleteRef.current[key]);
      if (isNowComplete && !wasComplete) {
        prevDayCompleteRef.current[key] = true;
        setUnlockCeremonyDay(d);
        break;
      }
      if (isNowComplete) {
        prevDayCompleteRef.current[key] = true;
      }
    }
  }, [weekId, isDayComplete, weekQuestCount]);

  useEffect(() => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      window.__triggerDoorUnlockCeremony = (day = 0) => setUnlockCeremonyDay(day);
      return () => {
        delete window.__triggerDoorUnlockCeremony;
      };
    }
  }, []);

  // Owner / super_admin: all stations always unlocked — no sequential gate
  const { currentUser } = useUserStore();
  const BYPASS_ROLES = ['super_admin', 'admin', 'teacher', 'team_leader', 'center_director'];
  const isOwnerBypass = BYPASS_ROLES.includes(currentUser?.role);

  // Sync teacherOverride once on mount when owner is detected
  useEffect(() => {
    if (isOwnerBypass) setTeacherOverride(true);
  }, [isOwnerBypass]);

  const pos = useCallback((station) =>
    isPortrait ? station.positionMobile : station.position, [isPortrait]);
  const taskPos = useCallback((station, qi) =>
    isPortrait ? station.taskPositionsMobile[qi] : station.taskPositions[qi], [isPortrait]);
  const midpoints = isPortrait ? PATH_MIDPOINTS_MOBILE : PATH_MIDPOINTS;

  const getStationCompletion = useCallback((dayIndex) => {
    const dayConfig = QUEST_SCHEDULE[dayIndex];
    if (!dayConfig) return { done: 0, total: 3, allDone: false };
    const done = dayConfig.quests.filter(q => isQuestCompleted(weekId, q.id)).length;
    return { done, total: dayConfig.quests.length, allDone: done === dayConfig.quests.length };
  }, [isQuestCompleted, weekId]);

  const isStationUnlocked = useCallback((dayIndex) => {
    return isDayUnlocked(weekId, dayIndex, teacherOverride);
  }, [isDayUnlocked, weekId, teacherOverride]);

  const suggestedIdx = STATIONS.findIndex((_, i) => {
    const c = getStationCompletion(i);
    return !c.allDone && isStationUnlocked(i);
  });
  const currentSuggestedIdx = suggestedIdx >= 0 ? suggestedIdx : 0;

  // Sequential flatten of all 15 quests in sequence Day 1 -> Day 5
  const allQuests = useMemo(() => QUEST_SCHEDULE.flatMap((day, dIdx) =>
    day.quests.map((q, qIdx) => ({
      ...q,
      dayIndex: dIdx,
      questIndex: qIdx,
      dayLabel: day.label,
      isDone: isQuestCompleted(weekId, q.id)
    }))
  ), [isQuestCompleted, weekId]);

  const nextUncompletedQuest = useMemo(() => {
    return allQuests.find(q => !q.isDone) || allQuests[0];
  }, [allQuests]);

  const handleStationClick = (stationIdx) => {
    if (!isStationUnlocked(stationIdx)) {
      setShowPIN(true);
      return;
    }
    setExpandedStation(expandedStation === stationIdx ? null : stationIdx);
  };

  const getTaskLink = (quest) => `/week/${weekId}/task/${quest.id}`;

  // Smart Context-Aware Concise Quotes from Lexio (Vertical Bubble Optimized)
  const dynamicQuotes = useMemo(() => {
    if (weekQuestCount >= totalQuests) {
      return [
        `Week ${weekId} 100% Done! 🏆`,
        "True Champion! 👑",
        "All shields earned! 🚀"
      ];
    }
    if (weekQuestCount === 0) {
      return [
        "Welcome! Ready for Day 1? 🌟",
        "Let's start our quest! 🗺️",
        "15m a day builds power! ⚡"
      ];
    }
    return [
      `Ready for ${nextUncompletedQuest.label}? 🌟`,
      `${weekQuestCount}/${totalQuests} quests done! 💪`,
      `${totalQuests - weekQuestCount} quests to Boss! 🏰`,
      "Practice makes perfect! ⚡"
    ];
  }, [weekQuestCount, totalQuests, nextUncompletedQuest, weekId]);

  const activeMascotQuote = dynamicQuotes[mascotQuoteIndex % dynamicQuotes.length];

  const handleMascotTap = () => {
    fireCelebrationConfetti('mascot_tap');
    setMascotMood('celebrate');
    setMascotQuoteIndex(prev => prev + 1);
    setTimeout(() => {
      setMascotMood(weekQuestCount >= totalQuests ? 'celebrate' : 'happy');
    }, 2000);
  };

  // Show narrative toast on first visit
  useEffect(() => {
    const key = `lexio_trip_${weekId}_intro_shown`;
    if (!localStorage.getItem(key)) {
      setTimeout(() => {
        setShowNarrative(true);
        localStorage.setItem(key, '1');
        setTimeout(() => setShowNarrative(false), 6000);
      }, 1200);
    }
  }, [weekId]);

  // Mascot celebrate when station changes
  useEffect(() => {
    if (currentSuggestedIdx !== prevSuggestedIdxRef.current && prevSuggestedIdxRef.current !== -1) {
      setMascotMood('celebrate');
      setTimeout(() => setMascotMood(weekQuestCount >= totalQuests ? 'celebrate' : 'happy'), 2000);
    } else {
      setMascotMood(weekQuestCount >= totalQuests ? 'celebrate' : weekQuestCount > 0 ? 'happy' : 'waving');
    }
    prevSuggestedIdxRef.current = currentSuggestedIdx;
  }, [currentSuggestedIdx, weekQuestCount, totalQuests]);

  return (
    <div className="qm3d-container">
      {/* Background */}
      <div className="qm3d-bg" style={{ backgroundImage: `url(${questMapBg})` }} />
      <div className="qm3d-overlay" />

      {/* Header */}
      <div className="qm3d-header">
        <div className="qm3d-header-left shrink-0">
          <button className="qm3d-hamburger" onClick={onToggleSidebar} aria-label="Menu">
            <Menu size={20} />
          </button>
          <div className="qm3d-logo">
            <span className="qm3d-logo-fox">🦊</span>
            <span className="qm3d-logo-text hidden sm:inline">LEXIO</span>
          </div>
        </div>
        {/* 1-Line Compact Trip Badge */}
        <div className="qm3d-week-badge shrink-0">
          <span className="qm3d-week-label">TRIP {weekId}</span>
          <span className="qm3d-week-dot">•</span>
          <span className="qm3d-progress-text">{weekQuestCount}/{totalQuests}</span>
        </div>

        {/* Quick Access Actions: Full buttons on sm+, Compact Hub button on mobile (<sm) */}
        <div className="qm3d-header-right shrink-0">
          {/* Desktop/Tablet (>= sm): 3 distinct action buttons with labels */}
          <div className="hidden sm:flex items-center gap-2">
            {weekId >= 17 && (
              <button
                type="button"
                onClick={() => navigate(`/week/${weekId}/chronicles`)}
                className="qm3d-action-btn bg-gradient-to-r from-amber-50 to-indigo-50 border border-amber-300 hover:border-amber-400 shadow-xs"
                title="Super Leo RPG"
              >
                <span className="text-sm">🦊</span>
                <span className="text-xs font-black text-amber-900">Super Leo</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/word-treasury')}
              className="qm3d-action-btn"
              title="Word Memory Bank"
            >
              <BookOpen size={16} className="text-purple-600" />
              <span className="text-xs font-black text-slate-700">Words</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCoopModal(true)}
              className="qm3d-action-btn"
              title="Class Co-op Board"
            >
              <Users size={16} className="text-amber-600" />
              <span className="text-xs font-black text-slate-700">Co-op</span>
            </button>
          </div>

          {/* Mobile (< sm): 1 compact Hub button to prevent header overflow */}
          <button
            type="button"
            onClick={() => setShowMobileQuickHub(true)}
            className="sm:hidden flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-indigo-950 border border-slate-200 rounded-xl shadow-xs backdrop-blur-md active:scale-95 transition"
            title="Quick Features Menu"
            aria-label="Quick Features Menu"
          >
            <Sparkles size={15} className="text-amber-500" />
            <span className="text-xs font-black text-slate-800">Hub</span>
          </button>
        </div>
      </div>

      {/* Narrative welcome toast */}
      {showNarrative && (
        <div className="qm3d-narrative-toast">
          <span className="qm3d-narrative-fox">🦊</span>
          <p className="qm3d-narrative-text">
            Welcome to <strong>Trip {weekId}</strong>! Your language adventure begins here.
            5 stations, 15 quests — starting from <strong>Story World</strong>! 🗺️✨
          </p>
        </div>
      )}

      {/* SVG Path connecting stations */}
      <svg className="qm3d-path-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          d={`M ${pos(STATIONS[0]).x} ${pos(STATIONS[0]).y}
              C ${pos(STATIONS[0]).x + (isPortrait ? 0 : 10)} ${pos(STATIONS[0]).y - (isPortrait ? 8 : 15)},
                ${pos(STATIONS[1]).x - (isPortrait ? 0 : 10)} ${pos(STATIONS[1]).y + (isPortrait ? 8 : 10)},
                ${pos(STATIONS[1]).x} ${pos(STATIONS[1]).y}
              C ${pos(STATIONS[1]).x + (isPortrait ? 0 : 10)} ${pos(STATIONS[1]).y + (isPortrait ? -8 : 5)},
                ${pos(STATIONS[2]).x - (isPortrait ? 0 : 10)} ${pos(STATIONS[2]).y - (isPortrait ? -8 : 5)},
                ${pos(STATIONS[2]).x} ${pos(STATIONS[2]).y}
              C ${pos(STATIONS[2]).x + (isPortrait ? 0 : 8)} ${pos(STATIONS[2]).y - (isPortrait ? 8 : 10)},
                ${pos(STATIONS[3]).x - (isPortrait ? 0 : 8)} ${pos(STATIONS[3]).y + (isPortrait ? 8 : 10)},
                ${pos(STATIONS[3]).x} ${pos(STATIONS[3]).y}
              C ${pos(STATIONS[3]).x + (isPortrait ? 0 : 8)} ${pos(STATIONS[3]).y - (isPortrait ? 8 : 8)},
                ${pos(STATIONS[4]).x - (isPortrait ? 0 : 8)} ${pos(STATIONS[4]).y + (isPortrait ? 8 : 8)},
                ${pos(STATIONS[4]).x} ${pos(STATIONS[4]).y}`}
          className="qm3d-path-line"
        />
        <path
          d={`M ${pos(STATIONS[0]).x} ${pos(STATIONS[0]).y}
              C ${pos(STATIONS[0]).x + (isPortrait ? 0 : 10)} ${pos(STATIONS[0]).y - (isPortrait ? 8 : 15)},
                ${pos(STATIONS[1]).x - (isPortrait ? 0 : 10)} ${pos(STATIONS[1]).y + (isPortrait ? 8 : 10)},
                ${pos(STATIONS[1]).x} ${pos(STATIONS[1]).y}
              C ${pos(STATIONS[1]).x + (isPortrait ? 0 : 10)} ${pos(STATIONS[1]).y + (isPortrait ? -8 : 5)},
                ${pos(STATIONS[2]).x - (isPortrait ? 0 : 10)} ${pos(STATIONS[2]).y - (isPortrait ? -8 : 5)},
                ${pos(STATIONS[2]).x} ${pos(STATIONS[2]).y}
              C ${pos(STATIONS[2]).x + (isPortrait ? 0 : 8)} ${pos(STATIONS[2]).y - (isPortrait ? 8 : 10)},
                ${pos(STATIONS[3]).x - (isPortrait ? 0 : 8)} ${pos(STATIONS[3]).y + (isPortrait ? 8 : 10)},
                ${pos(STATIONS[3]).x} ${pos(STATIONS[3]).y}
              C ${pos(STATIONS[3]).x + (isPortrait ? 0 : 8)} ${pos(STATIONS[3]).y - (isPortrait ? 8 : 8)},
                ${pos(STATIONS[4]).x - (isPortrait ? 0 : 8)} ${pos(STATIONS[4]).y + (isPortrait ? 8 : 8)},
                ${pos(STATIONS[4]).x} ${pos(STATIONS[4]).y}`}
          className="qm3d-path-line-glow"
        />
        {midpoints.map((mp, i) => {
          const stationUnlocked = isStationUnlocked(i + 1);
          return (
            <text
              key={i}
              x={mp.x}
              y={mp.y}
              className={`qm3d-path-arrow ${stationUnlocked ? 'unlocked' : 'locked'}`}
              transform={`rotate(${mp.angle}, ${mp.x}, ${mp.y})`}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              ›
            </text>
          );
        })}
      </svg>

      {/* Station Nodes */}
      {STATIONS.map((station, idx) => {
        const completion = getStationCompletion(idx);
        const unlocked = isStationUnlocked(idx);
        const isExpanded = expandedStation === idx;
        const isSuggested = idx === currentSuggestedIdx;
        const dayConfig = QUEST_SCHEDULE[idx];
        const stPos = pos(station);

        return (
          <div key={station.id}>
            {/* Station bubble */}
            <button
              className={`qm3d-station ${completion.allDone ? 'done' : ''} ${unlocked ? 'unlocked' : 'locked'} ${isSuggested ? 'suggested' : ''} ${isExpanded ? 'expanded' : ''}`}
              style={{
                left: `${stPos.x}%`,
                top: `${stPos.y}%`,
                '--station-color': station.color,
                '--station-glow': station.glowColor,
                '--station-bg': station.bgGradient,
              }}
              onClick={() => handleStationClick(idx)}
              aria-label={station.name}
            >
              {unlocked ? (
                <>
                  <span className="qm3d-station-emoji" style={{ fontSize: isSuggested ? '30px' : '24px' }}>
                    {station.emoji}
                  </span>
                  {completion.allDone && (
                    <span className="qm3d-station-mini-done">✓</span>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <span className="qm3d-station-index">{station.index}</span>
                  <Lock size={11} className="qm3d-station-lock opacity-70" />
                </div>
              )}
            </button>

            {/* Station label */}
            <div
              className={`qm3d-station-label ${unlocked ? 'unlocked' : 'locked-muted'}`}
              style={{
                left: `${stPos.x}%`,
                top: `${stPos.y + (isPortrait ? 5.5 : 7.5)}%`,
                '--station-color': station.color,
                '--station-glow': station.glowColor,
              }}
            >
              {unlocked ? (
                <>
                  <span className="qm3d-station-name-text">{station.emoji} {station.name}</span>
                  {completion.done > 0 && !completion.allDone && (
                    <span className="qm3d-station-count">{completion.done}/{completion.total}</span>
                  )}
                  {isSuggested && !completion.allDone && (
                    <div className="qm3d-start-here">
                      {idx === 0 && completion.done === 0 ? '▶ START HERE' : '▶ CONTINUE'}
                    </div>
                  )}
                </>
              ) : (
                <span className="qm3d-station-locked-name">
                  🔒 {station.name}
                </span>
              )}
            </div>

            {/* Expanded task dots — Desktop Only (Mobile uses Bottom Task Drawer) */}
            {!isPortrait && isExpanded && unlocked && dayConfig && (
              <>
                {dayConfig.quests.map((quest, qi) => {
                  const isDone = isQuestCompleted(weekId, quest.id);
                  const tPos = taskPos(station, qi);
                  return (
                    <div
                      key={quest.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(getTaskLink(quest));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(getTaskLink(quest)); }}
                      className={`qm3d-task-dot ${isDone ? 'done' : ''}`}
                      style={{
                        left: `${tPos.x}%`,
                        top: `${tPos.y}%`,
                        '--dot-color': station.color,
                        animationDelay: `${qi * 0.08}s`,
                      }}
                    >
                      {isDone ? (
                        <CheckCircle2 size={15} className="text-emerald-600" />
                      ) : (
                        <span className="qm3d-task-icon">{quest.icon}</span>
                      )}
                      <span className="qm3d-task-label">{quest.label}</span>
                      <span className="qm3d-task-time">~{quest.minutes}m</span>
                    </div>
                  );
                })}
                {/* Connector lines */}
                <svg className="qm3d-task-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {dayConfig.quests.map((_, qi) => {
                    const tPos = taskPos(station, qi);
                    return (
                      <line
                        key={qi}
                        x1={stPos.x} y1={stPos.y}
                        x2={tPos.x}  y2={tPos.y}
                        className="qm3d-task-connector"
                        style={{ animationDelay: `${qi * 0.08}s` }}
                      />
                    );
                  })}
                </svg>
              </>
            )}
          </div>
        );
      })}

      {/* Universal Task Drawer (Mobile bottom sheet / Desktop centered card) */}
      {expandedStation !== null && STATIONS[expandedStation] && (() => {
        const stationUnlockedForDrawer = isStationUnlocked(expandedStation);
        const dayQuestsForDrawer = QUEST_SCHEDULE[expandedStation]?.quests || [];
        // Find the first uncompleted quest in this day for the 'START HERE' indicator
        const firstUncompletedIdx = dayQuestsForDrawer.findIndex(q => !isQuestCompleted(weekId, q.id));
        return (
          <div className="qm3d-mobile-drawer-backdrop" onClick={() => setExpandedStation(null)}>
            <div className="qm3d-mobile-drawer" onClick={e => e.stopPropagation()}>
              <div className="qm3d-drawer-header">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{STATIONS[expandedStation].emoji}</span>
                  <div>
                    <h3 className="font-black text-slate-800 text-sm">{STATIONS[expandedStation].name}</h3>
                    <p className="text-[11px] font-bold text-slate-400">
                      Station {STATIONS[expandedStation].index} · {getStationCompletion(expandedStation).done}/{getStationCompletion(expandedStation).total} Hoàn thành
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setExpandedStation(null)} 
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 font-black text-sm"
                >
                  ✕
                </button>
              </div>

              <div className="qm3d-drawer-tasks space-y-2 mt-3">
                {dayQuestsForDrawer.map((quest, qi) => {
                  const isDone = isQuestCompleted(weekId, quest.id);
                  // Intra-day soft guide: visually highlight the recommended next quest
                  const isNextUp = !isDone && qi === firstUncompletedIdx;
                  // Quests after the next recommended one get a subtle 'waiting' style
                  const isWaiting = !isDone && qi > firstUncompletedIdx;
                  return (
                    <button
                      key={quest.id}
                      onClick={() => {
                        setExpandedStation(null);
                        navigate(getTaskLink(quest));
                      }}
                      className={`w-full p-3 rounded-2xl border-2 flex items-center justify-between text-left transition-all ${
                        isDone 
                          ? 'bg-emerald-50/70 border-emerald-300 text-emerald-900' 
                          : isNextUp
                            ? 'bg-amber-50 border-amber-400 text-slate-800 shadow-md ring-2 ring-amber-300/60'
                            : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-800 shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl w-8 text-center">{isDone ? '✅' : quest.icon}</span>
                        <div>
                          <p className="font-black text-xs leading-tight">{quest.label}</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-0.5">⏱️ ~{quest.minutes} phút · +{quest.xp || 50} XP</p>
                          {isNextUp && (
                            <p className="text-[10px] font-black text-amber-600 mt-0.5 animate-pulse">▶ Do this next!</p>
                          )}
                          {isWaiting && (
                            <p className="text-[10px] font-semibold text-slate-400 mt-0.5">✦ Available after above</p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1.5 rounded-xl text-[11px] font-black ${
                        isDone 
                          ? 'bg-emerald-600 text-white' 
                          : isNextUp
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'bg-slate-200 text-slate-600'
                      }`}>
                        {isDone ? 'Retry' : isNextUp ? 'Start ▶' : 'Next up'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Single Floating Duo-style Mascot Fox Companion (Transparent, Animated, with Bubble & Continue Button) */}
      <div
        className="qm3d-floating-mascot-hud"
        onClick={handleMascotTap}
        title="Tap Lexio for cheer!"
      >
        {/* Vertical Duo-style Speech Bubble with Inline Continue CTA */}
        <div className="qm3d-mascot-speech-bubble animate-in fade-in zoom-in-95 duration-200">
          <span className="qm3d-bubble-text">{activeMascotQuote}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(getTaskLink(nextUncompletedQuest));
            }}
            className="qm3d-bubble-cta"
            title={`Continue: ${nextUncompletedQuest.label}`}
          >
            <span>▶ Continue</span>
          </button>
          <div className="qm3d-bubble-tail" />
        </div>

        {/* Pure Transparent 3D Mascot Character (No circle backgrounds) */}
        <div className="qm3d-mascot-character">
          <LexioMascot
            size={isPortrait ? 76 : 92}
            mood={mascotMood}
          />
        </div>
      </div>

      {/* Victory Castle */}
      <div className="qm3d-victory-castle">
        <span className="qm3d-castle-icon">🏰</span>
        <span className="qm3d-castle-label">Victory (W156)</span>
      </div>

      {/* All done celebration */}
      {weekQuestCount >= totalQuests && (
        <div className="qm3d-all-done">
          <LexioMascot size={80} mood="celebrate" />
          <h2>🎉 Outstanding! Week Completed!</h2>
          <p>All {totalQuests} quests finished!</p>
        </div>
      )}

      {/* Modals */}
      {isArcadeOpen && <ArcadeModal weekNumber={weekId} onClose={() => setArcadeOpen(false)} />}
      <ClassLeaderboardModal isOpen={showCoopModal} onClose={() => setShowCoopModal(false)} />

      {/* Floating Chronicles FAB on Map for W17+ */}
      <ChroniclesFAB weekId={weekId} />

      {/* Mobile Quick Hub Bottom Sheet (Option 1) */}
      {showMobileQuickHub && (
        <div
          data-testid="mobile-quick-hub-overlay"
          className="fixed inset-0 z-[200] flex items-end justify-center bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200 sm:hidden"
          onClick={() => setShowMobileQuickHub(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-t-3xl p-5 shadow-2xl border-t-2 border-indigo-200 animate-in slide-in-from-bottom duration-250 flex flex-col gap-3.5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sheet Handle */}
            <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto" />

            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <h3 className="font-black text-slate-900 text-base">Quick Hub</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileQuickHub(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-black transition active:scale-90"
              >
                ✕
              </button>
            </div>

            {/* 3 Interactive Feature Cards */}
            <div className="grid grid-cols-1 gap-2.5">
              {/* 0. The Lexio Chronicles (W17+) */}
              {weekId >= 17 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMobileQuickHub(false);
                    navigate(`/week/${weekId}/chronicles`);
                  }}
                  className="flex items-center gap-3.5 p-3 rounded-2xl bg-gradient-to-r from-amber-50 via-indigo-50 to-purple-50 border border-amber-300 hover:border-amber-400 text-left transition active:scale-98 shadow-xs"
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-indigo-600 text-white flex items-center justify-center text-xl shadow-sm shrink-0">
                    🦊
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900 text-sm flex items-center gap-1.5">
                      <span>Super Leo</span>
                      <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full font-bold">RPG</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">Daily Rooms, Boss Gauntlet & Mascot Shop</p>
                  </div>
                  <span className="text-amber-500 text-sm font-black">→</span>
                </button>
              )}

              {/* 2. Word Treasury */}
              <button
                type="button"
                onClick={() => {
                  setShowMobileQuickHub(false);
                  navigate('/word-treasury');
                }}
                className="flex items-center gap-3.5 p-3 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 hover:border-purple-300 text-left transition active:scale-98 shadow-xs"
              >
                <div className="w-11 h-11 rounded-xl bg-purple-600 text-white flex items-center justify-center text-xl shadow-sm shrink-0">
                  📖
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-purple-950 text-sm flex items-center gap-1.5">
                    <span>Word Treasury</span>
                    <span className="text-[10px] bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded-full font-bold">Vocab</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">Review vocabulary & memory bank</p>
                </div>
                <span className="text-slate-400 text-sm font-black">→</span>
              </button>

              {/* 3. Class Co-op */}
              <button
                type="button"
                onClick={() => {
                  setShowMobileQuickHub(false);
                  setShowCoopModal(true);
                }}
                className="flex items-center gap-3.5 p-3 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 hover:border-amber-300 text-left transition active:scale-98 shadow-xs"
              >
                <div className="w-11 h-11 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center text-xl shadow-sm shrink-0">
                  🏆
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-amber-950 text-sm flex items-center gap-1.5">
                    <span>Class Co-op</span>
                    <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">Team Goal</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">Work together with friends for class milestones</p>
                </div>
                <span className="text-slate-400 text-sm font-black">→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Gate */}
      <ParentPINGate
        isOpen={showPIN}
        onClose={() => setShowPIN(false)}
        onSuccess={() => { setTeacherOverride(true); fireCelebrationConfetti?.('TeacherOverride'); }}
        title="Unlock Station"
        subtitle="Ask parent to enter PIN to unlock"
      />

      {/* Moment 4: Zone Complete Door Unlock Ceremony */}
      {unlockCeremonyDay !== null && (
        <DoorUnlockCeremony
          dayIndex={unlockCeremonyDay}
          weekId={weekId}
          onDone={() => setUnlockCeremonyDay(null)}
        />
      )}
    </div>
  );
}

